/**
 * AdjustLocationStep - Step 3 of the Location Wizard
 *
 * Features:
 * - Embedded Mapbox satellite-streets map with draggable pin
 * - Click-to-place support for repositioning the marker
 * - Reverse geocoding via Mapbox API to auto-fill address fields
 * - Address fields editable by user
 * - Latitude/longitude displayed as read-only from pin position
 */

import { useEffect, useRef, useCallback } from 'react'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, MapPin } from 'lucide-react'
import { useLocationWizardStore } from '@/stores/locationWizardStore'
import { useReverseGeocode } from '@/hooks/useReverseGeocode'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN

// Default center (US center) when no airport coordinates
const DEFAULT_CENTER: [number, number] = [-98.5795, 39.8283]
const DEFAULT_ZOOM = 4
// 90% of Mapbox max zoom (22) = 19.8
const AIRPORT_ZOOM = 14

export function AdjustLocationStep() {
  const {
    latitude,
    longitude,
    pinLatitude,
    pinLongitude,
    address_line1,
    city,
    state_province,
    postal_code,
    country,
    isReverseGeocoding,
    currentStep,
    setPinPosition,
    setAddressLine1,
    setCity,
    setStateProvince,
    setPostalCode,
    setCountry,
  } = useLocationWizardStore()

  const { geocode } = useReverseGeocode()

  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const markerRef = useRef<mapboxgl.Marker | null>(null)
  const mapInitializedRef = useRef(false)
  const hasFlownToAirportRef = useRef(false)

  // Handle pin position change (drag or click)
  const handlePinMove = useCallback(
    (lat: number, lng: number) => {
      setPinPosition(lat, lng)
      geocode(lat, lng)
    },
    [setPinPosition, geocode]
  )

  // Initialize map (lightweight, may init before coords are available)
  useEffect(() => {
    if (!mapContainerRef.current || mapInitializedRef.current || !MAPBOX_TOKEN) {
      return
    }

    mapboxgl.accessToken = MAPBOX_TOKEN

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    })

    // Add navigation controls
    map.addControl(new mapboxgl.NavigationControl(), 'top-right')

    // Add draggable marker (not placed on map yet)
    const marker = new mapboxgl.Marker({
      draggable: true,
      color: '#3b82f6',
    })

    // Handle marker drag end
    marker.on('dragend', () => {
      const lngLat = marker.getLngLat()
      handlePinMove(lngLat.lat, lngLat.lng)
    })

    // Handle click-to-place
    map.on('click', (e) => {
      const { lng, lat } = e.lngLat
      marker.setLngLat([lng, lat]).addTo(map)
      handlePinMove(lat, lng)
    })

    mapRef.current = map
    markerRef.current = marker
    mapInitializedRef.current = true

    return () => {
      map.remove()
      mapRef.current = null
      markerRef.current = null
      mapInitializedRef.current = false
      hasFlownToAirportRef.current = false
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // When step 3 becomes active, fly to airport coords, place pin, and trigger reverse geocode
  useEffect(() => {
    if (currentStep !== 2) return
    const map = mapRef.current
    const marker = markerRef.current
    if (!map || !marker) return

    // Resize first (container was hidden with display:none)
    map.resize()

    const hasCoords =
      typeof latitude === 'number' &&
      typeof longitude === 'number' &&
      !isNaN(latitude) &&
      !isNaN(longitude)

    if (hasCoords && !hasFlownToAirportRef.current) {
      const airportCenter: [number, number] = [longitude!, latitude!]

      // Wait for map to process resize before flying
      // (map needs valid dimensions after being unhidden)
      requestAnimationFrame(() => {
        // Place marker at airport coordinates
        marker.setLngLat(airportCenter).addTo(map)

        // Fly to airport location at close zoom
        map.flyTo({
          center: airportCenter,
          zoom: AIRPORT_ZOOM,
          duration: 1200,
        })

        // Set pin position in store and trigger reverse geocode
        setPinPosition(latitude!, longitude!)
        geocode(latitude!, longitude!)

        hasFlownToAirportRef.current = true
      })
    }
  }, [currentStep, latitude, longitude, setPinPosition, geocode])

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      {/* Map */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm font-medium">
            Click the map or drag the pin to set your FBO location
          </Label>
        </div>
        <div
          ref={mapContainerRef}
          className="w-full rounded-md border overflow-hidden"
          style={{ height: '350px' }}
        />
      </div>

      {/* Address Fields */}
      <div className="lg:w-[280px] space-y-3">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium">Address Details</Label>
          {isReverseGeocoding && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Looking up address...
            </span>
          )}
        </div>

        {/* Address Line */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Address</Label>
          <Input
            value={address_line1}
            onChange={(e) => setAddressLine1(e.target.value)}
            placeholder="123 Airport Blvd"
            className="h-8 text-sm"
          />
        </div>

        {/* City */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">City</Label>
          <Input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="Phoenix"
            className="h-8 text-sm"
          />
        </div>

        {/* State */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">
            State / Province
          </Label>
          <Input
            value={state_province}
            onChange={(e) => setStateProvince(e.target.value)}
            placeholder="AZ"
            className="h-8 text-sm"
          />
        </div>

        <div className="grid grid-cols-2 gap-2">
          {/* Postal Code */}
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Postal Code</Label>
            <Input
              value={postal_code}
              onChange={(e) => setPostalCode(e.target.value)}
              placeholder="85034"
              className="h-8 text-sm"
            />
          </div>

          {/* Country */}
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Country</Label>
            <Input
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="US"
              className="h-8 text-sm"
            />
          </div>
        </div>

        {/* Coordinates (read-only) */}
        <div className="pt-2 border-t">
          <Label className="text-xs text-muted-foreground mb-1 block">
            Coordinates
          </Label>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">Lat</Label>
              <Input
                value={
                  typeof pinLatitude === 'number' ? pinLatitude.toFixed(6) : ''
                }
                readOnly
                className="h-8 text-sm font-mono bg-muted"
                placeholder="--"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground">Lng</Label>
              <Input
                value={
                  typeof pinLongitude === 'number' ? pinLongitude.toFixed(6) : ''
                }
                readOnly
                className="h-8 text-sm font-mono bg-muted"
                placeholder="--"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
